import React, { useState } from 'react';
import { UserRole } from '../../types/roles';

interface SettingsSidebarProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  userRoles?: UserRole[];
}

interface MenuItem {
  id: string;
  label: string;
  icon?: string;
  subItems?: MenuItem[];
  allowedRoles?: UserRole[];
}

interface Category {
  id: string;
  label: string;
  items: MenuItem[];
  allowedRoles?: UserRole[];
}

const ADMIN_ROLES: UserRole[] = ['system_admin', 'clinical_director'];

const SettingsSidebar: React.FC<SettingsSidebarProps> = ({ activeSection, onSectionChange, userRoles = [] }) => {
  const [expandedCategories, setExpandedCategories] = useState<string[]>([]);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);

  const categories: Category[] = [
    {
      id: 'operations',
      label: 'OPERATIONS',
      items: [
        {
          id: 'practice',
          label: 'Practice',
          allowedRoles: ADMIN_ROLES,
          subItems: [
            { id: 'practice-details', label: 'Practice details' },
            { id: 'business-files', label: 'Business files' },
            { id: 'plan-info', label: 'Plan info' },
            { id: 'data-export', label: 'Data export' },
            { id: 'data-import', label: 'Data import' },
            { id: 'data-retention', label: 'Data retention' },
          ]
        },
        { id: 'team', label: 'Team Members', allowedRoles: ADMIN_ROLES },
        { id: 'roles-access', label: 'Roles & Access', allowedRoles: ADMIN_ROLES },
        { id: 'hr-management', label: 'HR Management', allowedRoles: [...ADMIN_ROLES, 'administrative_staff'] },
        { id: 'account-activity', label: 'Account activity' },
        { id: 'notifications', label: 'Notification preferences' },
      ]
    },
    {
      id: 'clientcare',
      label: 'CLIENT CARE',
      items: [
        { id: 'documentation', label: 'Documentation', allowedRoles: ADMIN_ROLES },
        { id: 'custom-measures', label: 'Custom Measures', allowedRoles: ADMIN_ROLES },
        { id: 'client-notifications', label: 'Client notifications' },
        { id: 'form-settings', label: 'New client forms' },
      ]
    },
    {
      id: 'billing',
      label: 'BILLING',
      allowedRoles: [...ADMIN_ROLES, 'administrative_staff'],
      items: [
        {
          id: 'client-billing',
          label: 'Client billing and insurance',
          subItems: [
            { id: 'client-billing-documents', label: 'Client billing documents' },
            { id: 'insurance-billing', label: 'Insurance billing' },
            { id: 'payers', label: 'Payers' },
          ]
        },
        { id: 'payment-processing', label: 'Payment processing' },
        { id: 'services-products', label: 'Services and products' },
      ]
    },
    {
      id: 'marketing',
      label: 'MARKETING',
      allowedRoles: ADMIN_ROLES,
      items: [
        { id: 'marketing-profile', label: 'Directory profile' },
        { id: 'marketing-website', label: 'Website and domain' },
      ]
    }
  ];

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories(prev =>
      prev.includes(categoryId)
        ? prev.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const toggleItem = (itemId: string) => {
    setExpandedItems(prev =>
      prev.includes(itemId)
        ? prev.filter(id => id !== itemId)
        : [...prev, itemId]
    );
  };

  // Filter categories and items based on user roles
  const hasRole = (allowedRoles?: UserRole[]) => {
    if (!allowedRoles || allowedRoles.length === 0) return true;
    if (userRoles.length === 0) return true; // No roles = show all (backwards compat)
    return allowedRoles.some(r => userRoles.includes(r));
  };

  const filteredCategories = categories
    .filter(cat => hasRole(cat.allowedRoles))
    .map(cat => ({
      ...cat,
      items: cat.items.filter(item => hasRole(item.allowedRoles))
    }))
    .filter(cat => cat.items.length > 0);

  return (
    <div style={{
      width: '280px',
      backgroundColor: '#0A2540',
      borderRight: '1px solid #6CA6C1',
      height: '100%',
      overflowY: 'auto',
      padding: '24px 0'
    }}>
      {filteredCategories.map(category => (
        <div key={category.id} style={{ marginBottom: '24px' }}>
          {/* Category Header */}
          <button
            onClick={() => toggleCategory(category.id)}
            style={{
              width: '100%',
              padding: '12px 24px',
              border: 'none',
              backgroundColor: 'transparent',
              color: '#F7E9A0',
              fontSize: '12px',
              fontWeight: 700,
              letterSpacing: '0.05em',
              textAlign: 'left',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              transition: 'background-color 0.2s'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#113F67'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
          >
            <span>{category.label}</span>
            <span style={{
              transform: expandedCategories.includes(category.id) ? 'rotate(180deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
              fontSize: '10px'
            }}>▼</span>
          </button>

          {/* Category Items */}
          {expandedCategories.includes(category.id) && (
            <div style={{ marginTop: '8px' }}>
              {category.items.map(item => (
                <div key={item.id}>
                  {/* Parent Item */}
                  <button
                    onClick={() => {
                      if (item.subItems) {
                        toggleItem(item.id);
                      } else {
                        onSectionChange(item.id);
                      }
                    }}
                    style={{
                      width: '100%',
                      padding: '12px 24px 12px 40px',
                      border: 'none',
                      backgroundColor: activeSection === item.id ? '#28527A' : 'transparent',
                      color: '#FFFFFF',
                      fontSize: '14px',
                      textAlign: 'left',
                      cursor: 'pointer',
                      transition: 'background-color 0.2s',
                      borderLeft: activeSection === item.id ? '3px solid #6CA6C1' : '3px solid transparent',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}
                    onMouseEnter={(e) => {
                      if (activeSection !== item.id) {
                        e.currentTarget.style.backgroundColor = '#113F67';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (activeSection !== item.id) {
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }
                    }}
                  >
                    <span>{item.label}</span>
                    {item.subItems && (
                      <span style={{
                        fontSize: '10px',
                        transform: expandedItems.includes(item.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s'
                      }}>
                        ▼
                      </span>
                    )}
                  </button>

                  {/* Sub Items */}
                  {item.subItems && expandedItems.includes(item.id) && (
                    <div style={{ marginTop: '4px' }}>
                      {item.subItems.map(subItem => (
                        <button
                          key={subItem.id}
                          onClick={() => onSectionChange(subItem.id)}
                          style={{
                            width: '100%',
                            padding: '10px 24px 10px 60px',
                            border: 'none',
                            backgroundColor: activeSection === subItem.id ? '#28527A' : 'transparent',
                            color: '#FFFFFF',
                            fontSize: '13px',
                            textAlign: 'left',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            borderLeft: activeSection === subItem.id ? '3px solid #6CA6C1' : '3px solid transparent'
                          }}
                          onMouseEnter={(e) => {
                            if (activeSection !== subItem.id) {
                              e.currentTarget.style.backgroundColor = '#113F67';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (activeSection !== subItem.id) {
                              e.currentTarget.style.backgroundColor = 'transparent';
                            }
                          }}
                        >
                          {subItem.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

export default SettingsSidebar;
